import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import type { YieldFetcher } from '../../types'

/**
 * Huma **PST** (PayFi Strategy Token) — realized supply APR, measured off the
 * Chainlink "PST-USDC Exchange Rate (Calculated)" feed on Ethereum.
 *
 * PST is a share of Huma 2.0's permissionless USDC pool on Solana, which funds
 * short-dated cross-border payment receivables (docs.huma.finance). Its yield
 * is paid as a RISING PST PRICE, and that price is produced by Huma's own
 * off-chain Price Oracle (a "dedicated service that tracks the price of $PST",
 * per their technical docs), then relayed on-chain by Chainlink. The bridged
 * Ethereum PST has no vault surface at all — it is a CCIP `BurnMintERC20` —
 * so the feed is the ONLY place the accrual is readable here.
 *
 * **Why the feed and not the 8 % headline.** Huma quotes "8 % APY, subject to
 * monthly adjustments"; nothing machine-readable publishes that number (no
 * DefiLlama pool under `huma`, no public stats API, and the app is US
 * geo-blocked). The feed, by contrast, keeps its FULL round history — unlike
 * Re's NAV feeds, whose `roundId` is pinned at 0 — so a trailing realized
 * rate needs no archival RPC: `getRoundData(round)` is a plain view. Measured
 * 2026-09-16 over all 1,505 rounds since 2026-05-25: monotonic (zero down
 * ticks), 7.70–7.79 % APR on every window from 7 days to inception, every
 * calendar month at 7.7 % — i.e. exactly the 8 % APY, compounded.
 *
 * **Why a 30-day window.** The feed is hourly but PAUSES: the longest gap was
 * 4.2 days, after which it catches up in one ~10 bps step. A short window
 * straddling a pause reads 0 % on one side and 40 % on the other; 30 days
 * absorbs it (the Wren lesson — a step-function NAV cannot be annualised off
 * the last two ticks).
 *
 * TRAPS:
 *  - **Read the PROXY, never the aggregator.** The aggregator
 *    (`0x7842fe22…`, `AccessControlledOCR2Aggregator`) reverts `No access`
 *    when `msg.sender` is a contract, so a Multicall3 batch against it fails
 *    outright. The proxy is on its access list and forwards fine.
 *  - Round ids on the proxy are PHASE-ENCODED (`phase << 64 | aggregatorRound`),
 *    so an offset is applied to the low 64 bits and re-joined with the phase.
 *    A phase change (aggregator upgrade) would make every historical probe
 *    revert until the new phase has a month of rounds; the fetcher then
 *    throws and the row reports 0 (= unknown), which is the honest answer.
 *  - `answer` is in 6 decimals (USDC per PST); only the ratio is used here.
 */

const CHAIN_ID = Chain.ETHEREUM_MAINNET

/** Chainlink `PST-USDC Exchange Rate (Calculated)` — the PROXY. */
export const HUMA_PST_FEED: Address =
  '0x4be50be32db1510240d542f77c5b36ca0d0965e6'

/** Registry `yieldKey`. */
export const HUMA_PST_KEY = 'HUMA::PST'
/** Canonical chain-1 token-list `assetGroup`, emitted alongside so the
 *  lending join resolves — PST is Morpho Blue and Fluid collateral on
 *  Ethereum, and until this fetcher existed both carried 0 % intrinsic
 *  yield on it. */
export const HUMA_PST_GROUP_KEY = 'PayFi Strategy Token::PST'

/** Trailing window the realized rate is measured over. */
export const HUMA_PST_WINDOW_SECONDS = 30 * 86_400
/** Below this the annualisation amplifies a single catch-up step. */
const MIN_WINDOW_SECONDS = 7 * 86_400
/** A feed that has not updated in this long is not a rate source. */
const MAX_STALENESS_SECONDS = 14 * 86_400
/**
 * Rounds back from the latest to probe. Cadence is ~hourly, so 30 days is
 * ~720 rounds when the feed never pauses and fewer when it does; the ladder
 * brackets the cutoff from both sides so the closest round ≥ 30 days old is
 * always among the candidates. Clamped at round 1 for a feed younger than
 * the window.
 */
const PROBE_OFFSETS = [560, 620, 680, 720, 760, 820, 900, 1000, 1200]

const YEAR_SECONDS = 365 * 86_400
const PHASE_SHIFT = 64n
const ROUND_MASK = (1n << PHASE_SHIFT) - 1n

const FEED_ABI = [
  {
    name: 'latestRoundData',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      { name: 'roundId', type: 'uint80' },
      { name: 'answer', type: 'int256' },
      { name: 'startedAt', type: 'uint256' },
      { name: 'updatedAt', type: 'uint256' },
      { name: 'answeredInRound', type: 'uint80' },
    ],
  },
  {
    name: 'getRoundData',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'roundId', type: 'uint80' }],
    outputs: [
      { name: 'roundId', type: 'uint80' },
      { name: 'answer', type: 'int256' },
      { name: 'startedAt', type: 'uint256' },
      { name: 'updatedAt', type: 'uint256' },
      { name: 'answeredInRound', type: 'uint80' },
    ],
  },
] as const

export interface FeedRound {
  answer: bigint
  updatedAt: number
}

const parseRound = (raw: unknown): FeedRound | undefined => {
  if (!raw) return undefined
  const tuple = Array.isArray(raw)
    ? raw
    : [
        (raw as { roundId?: unknown }).roundId,
        (raw as { answer?: unknown }).answer,
        (raw as { startedAt?: unknown }).startedAt,
        (raw as { updatedAt?: unknown }).updatedAt,
      ]
  const answer = tuple[1]
  const updatedAt = tuple[3]
  if (typeof answer !== 'bigint' || typeof updatedAt !== 'bigint')
    return undefined
  if (answer <= 0n || updatedAt <= 0n) return undefined
  return { answer, updatedAt: Number(updatedAt) }
}

/**
 * Pure: the annualised simple rate between the latest round and the best
 * historical candidate — the most recent round at least `windowSeconds` old,
 * or, for a feed younger than the window, the oldest round available.
 *
 * Throws rather than returning 0 when the inputs cannot support a rate: a
 * stale feed, a window under a week, or a ratio outside anything a
 * receivables pool can produce. The caller's `safeFetch` turns a throw into
 * "no rate" (the row then reads 0 = unknown), which is the correct answer for
 * every one of those.
 */
export const windowAprFromRounds = (
  latest: FeedRound,
  candidates: FeedRound[],
  nowSeconds: number,
  windowSeconds = HUMA_PST_WINDOW_SECONDS,
): number => {
  if (nowSeconds - latest.updatedAt > MAX_STALENESS_SECONDS) {
    throw new Error(
      `Huma PST: feed stale — last update ${nowSeconds - latest.updatedAt}s ago`,
    )
  }
  const cutoff = latest.updatedAt - windowSeconds
  const older = candidates
    .filter((c) => c.updatedAt < latest.updatedAt)
    .sort((a, b) => b.updatedAt - a.updatedAt)
  if (older.length === 0) throw new Error('Huma PST: no historical round')
  const start =
    older.find((c) => c.updatedAt <= cutoff) ?? older[older.length - 1]
  const dt = latest.updatedAt - start.updatedAt
  if (dt < MIN_WINDOW_SECONDS) {
    throw new Error(`Huma PST: window too short (${dt}s)`)
  }
  // 1e18-scaled growth ratio, then a float — the ratio is ~1.006 over a
  // month, so the double is exact to far more digits than the feed carries.
  const growth = Number((latest.answer * 10n ** 18n) / start.answer) / 1e18 - 1
  const apr = (growth * YEAR_SECONDS * 100) / dt
  // A negative is a write-down and MUST pass through; a three-digit figure
  // in either direction is a decoding error, not a rate.
  if (!Number.isFinite(apr) || Math.abs(apr) > 100) {
    throw new Error(`Huma PST: implausible rate ${apr}`)
  }
  return apr
}

const fetchPstAprOnChain = async (): Promise<number> => {
  const [latestRaw] = await multicallRetryUniversal({
    chain: CHAIN_ID,
    abi: FEED_ABI,
    calls: [{ address: HUMA_PST_FEED, name: 'latestRoundData', params: [] }],
    allowFailure: false,
  })
  const latestTuple = Array.isArray(latestRaw)
    ? latestRaw
    : [(latestRaw as { roundId?: unknown } | undefined)?.roundId]
  const latestRoundId = latestTuple[0]
  const latest = parseRound(latestRaw)
  if (typeof latestRoundId !== 'bigint' || !latest) {
    throw new Error('Huma PST: latestRoundData unreadable')
  }

  const phase = latestRoundId >> PHASE_SHIFT
  const aggRound = latestRoundId & ROUND_MASK
  const probeRounds = [
    ...new Set(
      PROBE_OFFSETS.map((off) => {
        const r = aggRound - BigInt(off)
        return r < 1n ? 1n : r
      }),
    ),
  ]
  const results = await multicallRetryUniversal({
    chain: CHAIN_ID,
    abi: FEED_ABI,
    calls: probeRounds.map((r) => ({
      address: HUMA_PST_FEED,
      name: 'getRoundData',
      params: [(phase << PHASE_SHIFT) | r],
    })),
    allowFailure: true,
  })
  const candidates = results
    .map(parseRound)
    .filter((r): r is FeedRound => r !== undefined)

  return windowAprFromRounds(latest, candidates, Math.floor(Date.now() / 1000))
}

export const humaPstFetcher: YieldFetcher = {
  label: 'HUMA_PST',
  fetch: async () => {
    const apr = await fetchPstAprOnChain()
    return {
      [HUMA_PST_KEY]: apr,
      [HUMA_PST_GROUP_KEY]: apr,
    }
  },
}
